// 사내 메신저 권한 정책 — 정회원 / 준회원 / 차단 / 탈퇴.
// RC 의 roles 배열에 우리 정의 role 을 추가해 분류.
//   - 'associate' = 준회원. 조직도에 없는 외부 가입자. UI 기능 대부분 차단.
//   - (default)   = 정회원. 일반 사용자.
//   - 'admin'     = 관리자.
//   - active=false = 차단/비활성.
// 준회원은 RC built-in 'guest' role(단독)로 생성 — 'guest' 유무로 판별.
//   (구버전 준회원은 'associate'/'guest-tpt' role 이었음 — 하위호환 위해 함께 검사)
// 정회원/관리자는 'user'(+부서 role)이라 'guest' 가 없으므로 영향 없음.

export type Membership = 'admin' | 'member' | 'associate' | 'inactive';

/** 준회원 계열 role — 서버 core/authz.GUEST_ROLES 와 같은 목록(클라 정본, 2026-10-03 공용화). 목록을 바꾸면 서버도 함께. */
export const GUEST_ROLES: readonly string[] = ['guest', 'associate', 'guest-tpt'];
/** roles 에 준회원 계열 role 이 하나라도 있는가 */
export const isGuestRoles = (roles: readonly string[] | null | undefined): boolean => (roles || []).some((r) => GUEST_ROLES.includes(r));

export function getMembership(roles: string[] | undefined, active: boolean | undefined = true): Membership {
	if (!active) return 'inactive';
	const r = roles || [];
	if (r.includes('admin')) return 'admin';
	if (isGuestRoles(r)) return 'associate';
	return 'member';
}

export interface MembershipCapability {
	canCreateTopic: boolean;        // 토픽 생성
	canCreateDM: boolean;            // 대화 시작 (외부에 먼저 거는 행위)
	canSeeOrgTree: boolean;          // 조직도 메뉴
	canSeeAdminPanel: boolean;       // 관리자 메뉴
	canShareDriveToOthers: boolean;  // 드라이브 → 채팅으로 보내기
	canInviteAssociate: boolean;     // 토픽방에서 준회원 초대 (모든 회원 가능)
	canManageDrive: boolean;         // 드라이브 폴더 생성 / 파일 업로드
	canUseAiWorkspace: boolean;      // AI 요약/RAG 워크스페이스 (번역·AI답글추천은 별개 — 준회원도 허용)
	canSeeAnnouncements: boolean;    // 전사/회사 공지 게시판·배너 (준회원 비노출, 2026-07-13)
	canUseIntegrations: boolean;     // 링크 모음·서비스 연동·외부 연동 섹션(TASK 등) (준회원 비노출, 2026-08-22)
	settingsOnly: boolean;           // 개인 설정만 가능
}

export function getCapability(m: Membership): MembershipCapability {
	if (m === 'admin') return {
		canCreateTopic: true, canCreateDM: true, canSeeOrgTree: true, canSeeAdminPanel: true,
		canShareDriveToOthers: true, canInviteAssociate: true, canManageDrive: true, canUseAiWorkspace: true, canSeeAnnouncements: true, canUseIntegrations: true, settingsOnly: false,
	};
	if (m === 'member') return {
		canCreateTopic: true, canCreateDM: true, canSeeOrgTree: true, canSeeAdminPanel: false,
		canShareDriveToOthers: true, canInviteAssociate: true, canManageDrive: true, canUseAiWorkspace: true, canSeeAnnouncements: true, canUseIntegrations: true, settingsOnly: false,
	};
	if (m === 'associate') return {
		// 준회원 — 토픽/대화 생성 불가, 초대 받은 방만. 조직도 안 보임. 드라이브 안 보임.
		// 준회원 초대도 불가 — 정회원 이상만 초대 가능(요구사항).
		canCreateTopic: false, canCreateDM: false, canSeeOrgTree: false, canSeeAdminPanel: false,
		canShareDriveToOthers: false, canInviteAssociate: false, canManageDrive: false, canUseAiWorkspace: false, canSeeAnnouncements: false, canUseIntegrations: false, settingsOnly: true,
	};
	// inactive (차단/탈퇴)
	return {
		canCreateTopic: false, canCreateDM: false, canSeeOrgTree: false, canSeeAdminPanel: false,
		canShareDriveToOthers: false, canInviteAssociate: false, canManageDrive: false, canUseAiWorkspace: false, canSeeAnnouncements: false, canUseIntegrations: false, settingsOnly: true,
	};
}

export function getMembershipLabel(m: Membership): string {
	return ({ admin: '관리자', member: '정회원', associate: '준회원', inactive: '비활성' } as const)[m];
}
